/**
 * Inquiry / signal pack — RAG output containers. Plan §26.12.
 *
 * Kaiyang Lesson §25.10 + ADR-0002 red line #4: LLM/RAG output is NEVER a
 * verdict. Two structural defenses:
 *   1. `InquiryPack.isVerdict = z.literal(false)` — verdict status cannot be
 *      represented by this schema at all.
 *   2. `InquiryPack.requiresHitl = z.literal(true)` — every inquiry implies a
 *      human-in-the-loop review step.
 *   3. `SignalPack.isCandidate = z.literal(true)` — signals never become
 *      canonical directly; only after a `ReviewDecision` transitions them.
 */

import { z } from "zod";

export const StructuredSignalsSchema = z.object({
  configFieldPaths: z.array(z.string()),
  matchedCardIds: z.array(z.string()),
  unresolvedUnknownFields: z.array(z.string()),
});
export type StructuredSignals = z.infer<typeof StructuredSignalsSchema>;

export const InquiryPackSchema = z.object({
  inquiryId: z.string().uuid(),
  questionTemplateId: z.string().min(1),
  questionText: z.object({ zh: z.string(), en: z.string() }),
  hypothesis: z.object({ zh: z.string(), en: z.string() }),
  structuredSignals: StructuredSignalsSchema,
  citationRefIds: z.array(z.string()),
  evidenceRefIds: z.array(z.string()),
  confidence: z.number().int().min(0).max(100),
  isVerdict: z.literal(false),
  requiresHitl: z.literal(true),
  createdAt: z.string(),
  askedBy: z.string(),
  relatedCardIds: z.array(z.string()),
});
export type InquiryPack = z.infer<typeof InquiryPackSchema>;

export const SignalPackSchema = z.object({
  signalId: z.string().min(1),
  source: z.enum([
    "wp29",
    "voc",
    "eur_lex",
    "unece",
    "member_state",
    "media",
    "other",
  ]),
  detectedAt: z.string(),
  payload: z.record(z.unknown()),
  proposedTargetObjectKind: z.enum(["rule", "evidence", "card"]),
  isCandidate: z.literal(true),
  requiresHitl: z.literal(true),
  bundleId: z.string().optional(),
  sourceUrl: z.string().url(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type SignalPack = z.infer<typeof SignalPackSchema>;
